import { CellSignalFull, CellSignalLow, CellSignalMedium, Minus, WarningOctagon } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { cx } from "@/lib/cx";

/**
 * Priority is stored as 0 = none, 1 = P0 (urgent) ... 4 = P3 (low). Every label in the UI goes
 * through `priorityLabel`, so the instance setting `priorityStyle` ("p": P0 to P3, "word": Urgent
 * to Low; same names as the server and `/me`) switches all of them at once.
 */
export type PriorityStyle = "p" | "word";
let style: PriorityStyle = "p";
export function setPriorityStyle(next: PriorityStyle) {
  style = next;
}

/** Picker and board order: most urgent first, "none" last. */
export const PRIORITY_VALUES = [1, 2, 3, 4, 0] as const;

/** The word for a priority ("Urgent"), used as a secondary hint next to "P0". */
export function priorityWord(value: number): string {
  switch (value) {
    case 1:
      return m.priority_urgent();
    case 2:
      return m.priority_high();
    case 3:
      return m.priority_medium();
    case 4:
      return m.priority_low();
    default:
      return m.priority_none();
  }
}

/** Primary label: "P0".."P3" (or the word, per instance style); "No priority" for 0. */
export function priorityLabel(value: number): string {
  if (value < 1 || value > 4) return m.priority_none();
  return style === "p" ? `P${value - 1}` : priorityWord(value);
}

/** Query-language value for a priority ("p0", "none"). */
export const priorityToken = (value: number) => (value >= 1 && value <= 4 ? `p${value - 1}` : "none");

const color: Record<number, string> = {
  0: "text-priority-none",
  1: "text-priority-urgent",
  2: "text-priority-high",
  3: "text-priority-medium",
  4: "text-priority-low",
};

/**
 * Priority glyph: an octagon for P0, signal bars (3, 2, 1) for P1 to P3, a dash for none. Always
 * paired with text: either `showLabel` or the accessible name.
 */
export function PriorityIcon({
  value,
  size = 14,
  showLabel,
  className,
}: {
  value: number;
  size?: number;
  showLabel?: boolean;
  className?: string;
}) {
  const label = priorityLabel(value);
  const props = { size, "aria-hidden": true as const, className: cx("shrink-0", color[value] ?? color[0]) };
  const glyph =
    value === 1 ? (
      <WarningOctagon {...props} weight="fill" />
    ) : value === 2 ? (
      <CellSignalFull {...props} />
    ) : value === 3 ? (
      <CellSignalMedium {...props} />
    ) : value === 4 ? (
      <CellSignalLow {...props} />
    ) : (
      <Minus {...props} />
    );
  if (showLabel) {
    return (
      <span className={cx("inline-flex min-w-0 items-center gap-1.5", className)}>
        {glyph}
        <span className="truncate-title">{label}</span>
      </span>
    );
  }
  return (
    <span role="img" aria-label={label} title={label} className={cx("inline-flex shrink-0", className)}>
      {glyph}
    </span>
  );
}
